import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useGetPermitions } from '@/services';
import { AuthContextType, UserScreens } from '@/types/generals';

// Create context with appropriate type
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, signOut } = useAuthenticator(context => [context.user]);
  const {
    data: permissionsData,
    loading,
    getUserPermissions
  } = useGetPermitions({ username: user?.username || '' });

  const [permissions, setPermissions] = useState<UserScreens>({
    flagIgnore: false,
    permissions: []
  });

  useEffect(() => {
    if (user?.username) {
      getUserPermissions();
    }
  }, [getUserPermissions, user?.username]);


  useEffect(() => {
    if (permissionsData?.permissions) {
      setPermissions(permissionsData);
    }
  }, [permissionsData]);

  const hasPermission = (screenName: string) => {
    const perm = permissions.permissions.find(p => p.screenName === screenName);
    if (!perm) return { canRead: false, canWrite: false, noScreen: true };
    return { canRead: !!perm.canRead, canWrite: !!perm.canWrite, noScreen: false };
  };

  return (
    <AuthContext.Provider
      value={{
        user: user
          ? {
            username: user.username,
            userId: user.userId ?? user.signInDetails?.loginId ?? 'unknown',
          }
          : null,
        permissions,
        loading,
        error: permissionsData?.error ?? null,
        signOut,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
